import {
  Badge,
  Button,
  Form,
  FormControl,
  FormDisplayField,
  FormField,
  FormItem,
  FormLabel,
  FormLayout,
  FormMessage,
  FormSection,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Textarea,
  toast,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, CheckCircle, Play, XCircle } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAiPlatform } from '../AiPlatformContext';
import type { InvokeToolAuth, LlmConnection, ToolConfig } from '../AiPlatformService';

// ─── Schema editor ────────────────────────────────────────────────────────────

function SchemaEditor({ tool, onSaved }: { tool: ToolConfig; onSaved: (t: ToolConfig) => void }) {
  const { service } = useAiPlatform();
  const [raw, setRaw] = React.useState(() => JSON.stringify(tool.jsonSchema ?? {}, null, 2));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function handleSave() {
    setError(null);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(raw) as Record<string, unknown>;
    } catch (err) {
      setError(err instanceof SyntaxError ? err.message : 'Invalid JSON');
      return;
    }
    setSaving(true);
    try {
      const updated = await service.updateTool(tool.toolId, { jsonSchema: parsed });
      onSaved(updated);
      toast.success('Schema saved');
    } catch {
      setError('Failed to save schema');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        JSON Schema describing the tool's input parameters.
      </p>
      <Textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        rows={12}
        className="font-mono text-base sm:text-xs"
        spellCheck={false}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end">
        <Button type="button" size="sm" onClick={() => void handleSave()} disabled={saving}>
          {saving ? <Spinner className="mr-2 size-4" /> : null}
          {saving ? 'Saving…' : 'Save Schema'}
        </Button>
      </div>
    </div>
  );
}

// ─── Test panel ───────────────────────────────────────────────────────────────

type JsonSchema = Record<string, unknown>;
interface ValidationError {
  path: string;
  message: string;
}

function validateAgainstSchema(value: unknown, schema: JsonSchema): ValidationError[] {
  const errors: ValidationError[] = [];
  if (schema.type === 'object' || schema.properties) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      errors.push({ path: '(root)', message: 'Expected an object.' });
      return errors;
    }
    const obj = value as Record<string, unknown>;
    for (const key of (schema.required as string[]) ?? []) {
      if (!(key in obj) || obj[key] === undefined || obj[key] === null)
        errors.push({ path: key, message: `"${key}" is required.` });
    }
    for (const [k, ps] of Object.entries(
      (schema.properties as Record<string, { type?: string }>) ?? {},
    )) {
      const v = obj[k];
      if (v === undefined || v === null) continue;
      if (ps.type === 'string' && typeof v !== 'string')
        errors.push({ path: k, message: `"${k}" must be a string.` });
      else if (ps.type === 'number' && typeof v !== 'number')
        errors.push({ path: k, message: `"${k}" must be a number.` });
      else if (ps.type === 'boolean' && typeof v !== 'boolean')
        errors.push({ path: k, message: `"${k}" must be a boolean.` });
      else if (ps.type === 'array' && !Array.isArray(v))
        errors.push({ path: k, message: `"${k}" must be an array.` });
      else if (ps.type === 'object' && (typeof v !== 'object' || Array.isArray(v)))
        errors.push({ path: k, message: `"${k}" must be an object.` });
    }
  }
  return errors;
}

const NAMED_SAMPLES: Record<string, Record<string, unknown>> = {
  'ado.query_workitems': {
    wiql: "SELECT [System.Id],[System.Title],[System.State],[System.AssignedTo] FROM WorkItems WHERE [System.TeamProject] = 'ARAF' ORDER BY [System.ChangedDate] DESC",
    fields: ['System.Id', 'System.Title', 'System.State', 'System.AssignedTo'],
  },
};

/** Builds a skeleton JSON payload from a JSON Schema's properties. */
function buildPrefill(schema: JsonSchema): Record<string, unknown> {
  // Support both standard JSON Schema ({properties: {...}}) and
  // function-calling format ({parameters: {properties: {...}}})
  const rawProps =
    (schema.properties as Record<string, JsonSchema> | undefined) ??
    ((schema.parameters as JsonSchema | undefined)?.properties as
      | Record<string, JsonSchema>
      | undefined);
  if (!rawProps) return {};
  const props = rawProps;
  const out: Record<string, unknown> = {};
  for (const [key, prop] of Object.entries(props)) {
    const ex = prop.example ?? prop.default;
    if (ex !== undefined) {
      out[key] = ex;
    } else if (Array.isArray(prop.enum) && prop.enum.length > 0) {
      out[key] = prop.enum[0];
    } else {
      switch (prop.type) {
        case 'string':
          out[key] = '';
          break;
        case 'number':
        case 'integer':
          out[key] = 0;
          break;
        case 'boolean':
          out[key] = false;
          break;
        case 'array':
          out[key] = [];
          break;
        case 'object':
          out[key] = {};
          break;
        default:
          out[key] = null;
      }
    }
  }
  return out;
}

type AuthMode = 'integration' | 'manual';

function TestPanel({ tool }: { tool: ToolConfig }) {
  const { service } = useAiPlatform();
  const inputSchema = (tool.jsonSchema ?? null) as JsonSchema | null;

  // ── Auth state ────────────────────────────────────────────────────────────
  const [authMode, setAuthMode] = React.useState<AuthMode>('integration');
  const [connections, setConnections] = React.useState<LlmConnection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = React.useState(true);
  const [selectedIntegrationId, setSelectedIntegrationId] = React.useState<string>('');
  const [manualApiKey, setManualApiKey] = React.useState('');
  const [manualBaseUrl, setManualBaseUrl] = React.useState('');

  // ── Input params state ────────────────────────────────────────────────────
  const [rawInput, setRawInput] = React.useState(() => {
    if (NAMED_SAMPLES[tool.name]) return JSON.stringify(NAMED_SAMPLES[tool.name], null, 2);
    if (inputSchema) return JSON.stringify(buildPrefill(inputSchema), null, 2);
    return '{}';
  });
  const [parseError, setParseError] = React.useState<string | null>(null);
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [isValid, setIsValid] = React.useState<boolean | null>(null);
  const [invoking, setInvoking] = React.useState(false);
  const [response, setResponse] = React.useState<unknown>(null);
  const [invokeError, setInvokeError] = React.useState<string | null>(null);

  // Load connections once
  React.useEffect(() => {
    void service.listConnections().then((list) => {
      setConnections(list);
      const firstActive = list.find((c) => c.isActive);
      if (firstActive) setSelectedIntegrationId(firstActive.id);
      setConnectionsLoading(false);
    });
  }, [service]);

  function handleValidate() {
    setParseError(null);
    setValidationErrors([]);
    setIsValid(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawInput);
    } catch (err) {
      setParseError(err instanceof SyntaxError ? err.message : 'Invalid JSON.');
      setIsValid(false);
      return;
    }
    if (inputSchema) {
      const errs = validateAgainstSchema(parsed, inputSchema);
      if (errs.length > 0) {
        setValidationErrors(errs);
        setIsValid(false);
        return;
      }
    }
    setIsValid(true);
  }

  async function handleInvoke() {
    if (!isValid) return;
    setInvoking(true);
    setInvokeError(null);
    setResponse(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawInput);
    } catch {
      setInvokeError('Input is not valid JSON.');
      setInvoking(false);
      return;
    }
    const auth: InvokeToolAuth =
      authMode === 'integration'
        ? { integrationId: selectedIntegrationId || undefined }
        : {
            apiKey: manualApiKey || undefined,
            baseUrl: manualBaseUrl || undefined,
          };
    try {
      setResponse(await service.invokeTool(tool.toolId, parsed, auth));
    } catch (err) {
      setInvokeError(err instanceof Error ? err.message : 'Invocation failed.');
    } finally {
      setInvoking(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* ── Auth ─────────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Authentication
        </p>
        <RadioGroup
          value={authMode}
          onValueChange={(v) => setAuthMode(v as AuthMode)}
          className="flex gap-4"
        >
          <label
            htmlFor={`auth-integration-${tool.toolId}`}
            className="flex cursor-pointer items-center gap-2 text-sm"
          >
            <RadioGroupItem id={`auth-integration-${tool.toolId}`} value="integration" />
            Use existing integration
          </label>
          <label
            htmlFor={`auth-manual-${tool.toolId}`}
            className="flex cursor-pointer items-center gap-2 text-sm"
          >
            <RadioGroupItem id={`auth-manual-${tool.toolId}`} value="manual" />
            Manual
          </label>
        </RadioGroup>

        {authMode === 'integration' && (
          <div className="space-y-1.5">
            {connectionsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner className="size-3.5" />
                Loading integrations…
              </div>
            ) : connections.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No integrations configured. Set one up in the LLM Connections tab or switch to
                Manual.
              </p>
            ) : (
              <Select value={selectedIntegrationId} onValueChange={setSelectedIntegrationId}>
                <SelectTrigger className="w-full max-w-xs">
                  <SelectValue placeholder="Select integration…" />
                </SelectTrigger>
                <SelectContent>
                  {connections.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                        {c.name}
                        {!c.isActive && (
                          <Badge variant="warning" appearance="soft" size="sm">
                            not configured
                          </Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {authMode === 'manual' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">API Key</p>
              <Input
                type="password"
                value={manualApiKey}
                onChange={(e) => setManualApiKey(e.target.value)}
                placeholder="sk-…"
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Base URL (optional)</p>
              <Input
                value={manualBaseUrl}
                onChange={(e) => setManualBaseUrl(e.target.value)}
                placeholder="https://api.openai.com/v1"
                className="font-mono text-sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Input params ─────────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Input Parameters (JSON)
        </p>
        <Textarea
          value={rawInput}
          onChange={(e) => {
            setRawInput(e.target.value);
            setIsValid(null);
            setParseError(null);
            setValidationErrors([]);
          }}
          rows={10}
          className="font-mono text-sm"
          placeholder='{"key": "value"}'
        />
      </div>

      {parseError && (
        <p className="text-sm text-destructive" role="alert">
          <span className="font-semibold">Parse error: </span>
          {parseError}
        </p>
      )}

      {validationErrors.length > 0 && (
        <div
          className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm"
          role="alert"
        >
          <p className="mb-1 font-semibold text-destructive">Validation errors:</p>
          <ul className="space-y-0.5">
            {validationErrors.map((e) => (
              <li
                key={`${e.path}-${e.message}`}
                className="flex items-start gap-1.5 text-destructive"
              >
                <XCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span>
                  <span className="font-mono">{e.path}</span>: {e.message}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isValid === true && (
        <div className="flex items-center gap-1.5 text-sm text-success" role="status">
          <CheckCircle className="size-4" aria-hidden />
          Validation passed.
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={handleValidate}>
          Validate
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void handleInvoke()}
          disabled={invoking || isValid !== true}
        >
          {invoking ? (
            <Spinner className="mr-2 size-4" />
          ) : (
            <Play className="mr-2 size-4" aria-hidden />
          )}
          {invoking ? 'Invoking…' : 'Invoke'}
        </Button>
      </div>

      {invokeError && (
        <p className="text-sm text-destructive" role="alert">
          {invokeError}
        </p>
      )}

      {response !== null && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Response
          </p>
          <div className="rounded-lg border bg-muted/20 p-3">
            <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">
              {JSON.stringify(response, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  scope: z.enum(['PUBLIC', 'APPLICATION', 'TENANT']).optional(),
  status: z.enum(['ACTIVE', 'DRAFT', 'DEPRECATED']).optional(),
});

type FormValues = z.infer<typeof formSchema>;

export function ToolDetailPanel({ toolId, onBack }: { toolId: string; onBack: () => void }) {
  const { service } = useAiPlatform();
  const [tool, setTool] = React.useState<ToolConfig | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onTouched',
    defaultValues: { name: '', description: '', scope: undefined, status: undefined },
  });

  React.useEffect(() => {
    setLoading(true);
    void service.getTool(toolId).then((t) => {
      setTool(t);
      form.reset({
        name: t.name,
        description: t.description ?? '',
        scope: t.scope,
        status: t.status,
      });
      setLoading(false);
    });
  }, [service, toolId, form]);

  async function handleValid(values: FormValues) {
    if (!tool) return;
    setSaving(true);
    try {
      const updated = await service.updateTool(tool.toolId, {
        name: values.name,
        description: values.description,
        scope: values.scope as ToolConfig['scope'],
        status: values.status as ToolConfig['status'],
      });
      setTool(updated);
      form.reset({
        name: updated.name,
        description: updated.description ?? '',
        scope: updated.scope,
        status: updated.status,
      });
      toast.success('Tool updated');
    } catch {
      toast.error('Failed to update tool');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-6 w-40" />
        {Array.from({ length: 5 }, (_, i) => `sk-${i}`).map((k) => (
          <Skeleton key={k} className="h-12 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="max-w-2xl space-y-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back
        </button>
        <p className="text-sm text-destructive">Tool not found.</p>
      </div>
    );
  }

  const isDirty = form.formState.isDirty;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back
        </button>
        <span className="text-muted-foreground">/</span>
        <p className="truncate text-sm font-semibold text-foreground">{tool.name}</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleValid)}>
          <FormLayout mode="edit">
            <FormSection title="Identity" asCard>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} className="font-mono" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="Configuration" columns={2} asCard>
              <FormField
                control={form.control}
                name="scope"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scope</FormLabel>
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select scope" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="PUBLIC">Public</SelectItem>
                        <SelectItem value="APPLICATION">Application</SelectItem>
                        <SelectItem value="TENANT">Tenant</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="DRAFT">Draft</SelectItem>
                        <SelectItem value="DEPRECATED">Deprecated</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="Metadata" columns={2} density="compact" asCard>
              <FormDisplayField
                className="col-span-2"
                label="ID"
                value={tool.toolId}
                copyValue={tool.toolId}
                mono
              />
              {tool.appSlug && <FormDisplayField label="Application" value={tool.appSlug} mono />}
              {tool.controllerSlug && (
                <FormDisplayField label="Group" value={tool.controllerSlug} mono />
              )}
              {tool.tenantSlug && <FormDisplayField label="Tenant" value={tool.tenantSlug} mono />}
              {tool.version && <FormDisplayField label="Version" value={tool.version} mono />}
              {tool.gitPath && <FormDisplayField label="Git Path" value={tool.gitPath} mono />}
              {tool.gitSha && <FormDisplayField label="Git SHA" value={tool.gitSha} mono />}
              {tool.createdAt && (
                <FormDisplayField
                  label="Created"
                  value={new Date(tool.createdAt).toLocaleString()}
                />
              )}
              {tool.updatedAt && (
                <FormDisplayField
                  label="Updated"
                  value={new Date(tool.updatedAt).toLocaleString()}
                />
              )}
            </FormSection>

            {isDirty && (
              <div className="flex items-center gap-3">
                <Button type="submit" size="sm" disabled={saving}>
                  {saving && <Spinner className="mr-2 size-4" />}
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => form.reset()}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            )}
          </FormLayout>
        </form>
      </Form>

      <FormSection title="Schema" asCard>
        <SchemaEditor key={tool.toolId} tool={tool} onSaved={setTool} />
      </FormSection>

      <FormSection title="Test" asCard>
        <TestPanel tool={tool} />
      </FormSection>
    </div>
  );
}
